// The human-readable label for the dashboard's global Stages selection. Pure and dependency-free
// (types only, erased at build time) so it can be read from the filter bar itself and from anywhere
// that has to say *what* it is currently scoped to — the material mapping modal's Emissions tab
// (RTLCA-5397) — without either copy drifting from the other.
import type { StageGroupType } from "./types/DashboardType";

// The selection stores the combined production module as `A1A3`; the dash is display-only.
export function prettyStageCode(value: string): string {
  return value === "A1A3" ? "A1-A3" : value;
}

// The Building process (A4-A5) stages, split out of the Total for BR18 2025.
export const BUILDING_PROCESS_STAGES = ["A4", "A5"];

// Shown on the Building process line when the Stages filter leaves out both A4 and A5.
export const BUILDING_PROCESS_FILTERED_OUT = "Not included in the Stages filter";

// True when the filter leaves out every Building process stage. An empty selection is "no filter".
export function excludesBuildingProcess(selected: string[] | undefined): boolean {
  const selection = selected ?? [];
  return selection.length > 0 && !selection.some((value) => BUILDING_PROCESS_STAGES.includes(value));
}

// What "nothing is narrowed" reads as. Returned for an empty selection and for a full one alike, and
// used by callers as the signal to suppress a "scoped to …" note entirely.
export const ALL_STAGES_SUMMARY = "All";

// Beyond this many parts the summary is truncated with a "+n" tail rather than growing unbounded.
const MAX_PARTS = 3;

const truncate = (parts: string[]): string =>
  parts.length <= MAX_PARTS ? parts.join(", ") : `${parts.slice(0, MAX_PARTS).join(", ")} +${parts.length - MAX_PARTS}`;

/**
 * Summarize the selected stages against the stage groups the project offers: `"A1-A3"`,
 * `"A1-A3, A4"`, `"A1-A3, A4, B1 +2"`, or `ALL_STAGES_SUMMARY`.
 *
 * A group whose stages are ALL selected collapses to a `first-last` range, which is what makes the
 * common cases read as "A1-A3" and "C1-C4" instead of listing every module.
 *
 * An empty selection means "no stage filter" rather than "no stages", matching the store, which
 * sends no stages at all to the API when nothing is selected; a fully-populated selection is
 * treated as the same thing defensively, since it is equivalent even though it is not what gets
 * stored.
 */
export function summarizeSelectedStages(selected: string[] | undefined, stageGroups: StageGroupType[] | undefined): string {
  const selection = selected ?? [];
  const groups = stageGroups ?? [];
  const totalStages = groups.reduce((acc, group) => acc + group.stages.length, 0);

  if (!selection.length || selection.length === totalStages) return ALL_STAGES_SUMMARY;

  // No groups to interpret the selection against — the store hasn't loaded them yet. The selection
  // is still scoping the data, so the stages are listed as stored rather than summarized away to
  // an empty string, which is what a group-only walk produces and what would have read as
  // "Scoped to the Stages filter: ." in the mapping modal.
  if (!totalStages) return truncate(selection.map(prettyStageCode));

  const parts: string[] = [];
  for (const group of groups) {
    const groupValues = group.stages.map((stage) => stage.value);
    const selectedInGroup = groupValues.filter((value) => selection.includes(value));
    if (selectedInGroup.length === 0) continue;
    if (selectedInGroup.length === groupValues.length && groupValues.length > 1) {
      parts.push(`${prettyStageCode(groupValues[0])}-${prettyStageCode(groupValues[groupValues.length - 1])}`);
    } else {
      parts.push(...selectedInGroup.map(prettyStageCode));
    }
  }

  return truncate(parts);
}
